export type HistoryItem = {
  id: string;
  transcript: string;
  sender: "user" | "assistant";
  timestamp: Date; // Add timestamp field
  groundingFiles?: any[]; // Optional grounding files from RAG
};

export type SessionUpdateCommand = {
  type: "session.update";
  session: {
    modalities?: ("text" | "audio")[];
    instructions?: string;
    voice?: "alloy" | "ash" | "ballad" | "coral" | "echo" | "sage" | "shimmer" | "verse";
    input_audio_format?: "pcm16" | "g711_ulaw" | "g711_alaw";
    output_audio_format?: "pcm16" | "g711_ulaw" | "g711_alaw";
    input_audio_transcription?: {
      model: "whisper-1" | "gpt-4o-transcribe";
    };
    turn_detection?: {
      type: "server_vad" | "none";
      threshold?: number;
      prefix_padding_ms?: number;
      silence_duration_ms?: number;
      create_response?: boolean;
    };
    tools?: Array<{
      type: "function";
      name: string;
      description?: string;
      parameters?: Record<string, any>;
    }>;
    tool_choice?: "auto" | "none" | "required" | { type: "function"; name: string };
    temperature?: number;
    max_response_output_tokens?: number | "inf";
    conversation?: {
      messages?: Array<{
        type: string;
        item: {
          type: string;
          role: string;
          content: {
            type: string;
            text: string;
          }[];
        };
      }>;
    };
    // Legacy support
    messages?: Array<{
      type: string;
      item: {
        type: string;
        role: string;
        content: {
          type: string;
          text: string;
        }[];
      };
    }>;
  };
};

export type InputAudioBufferAppendCommand = {
  type: "input_audio_buffer.append";
  audio: string;
};

export type InputAudioBufferClearCommand = {
  type: "input_audio_buffer.clear";
};

export type Message = {
  type: string;
  event_id?: string;
};

export type ResponseAudioDelta = {
  type: "response.audio.delta";
  delta: string;
};

export type ResponseAudioTranscriptDelta = {
  type: "response.audio_transcript.delta";
  delta: string;
  event_id: string;
};

export type ResponseInputAudioTranscriptionCompleted = {
  type: "conversation.item.input_audio_transcription.completed";
  event_id: string;
  item_id: string;
  content_index: number;
  transcript: string;
};

export type ResponseDone = {
  type: "response.done";
  event_id: string;
  response: {
    id: string;
    output: { id: string; content?: { transcript: string; type: string }[] }[];
  };
};

export type ExtensionMiddleTierToolResponse = {
  type: "extension.middle_tier_tool_response";
  previous_item_id: string;
  tool_name: string;
  tool_result: string; // JSON string that needs to be parsed into ToolResult
};

// Session events
export type SessionCreated = {
  type: "session.created";
  event_id: string;
  session: {
    id: string;
    object: string;
    model: string;
    expires_at: number;
    modalities: string[];
    instructions: string;
    voice: string;
    input_audio_format: string;
    output_audio_format: string;
    input_audio_transcription: object | null;
    turn_detection: object;
    tools: object[];
    tool_choice: string;
    temperature: number;
    max_response_output_tokens: number | null;
  };
};

// Input audio buffer events
export type InputAudioBufferSpeechStopped = {
  type: "input_audio_buffer.speech_stopped";
  event_id: string;
  audio_end_ms: number;
  item_id: string;
};

export type InputAudioBufferCommitted = {
  type: "input_audio_buffer.committed";
  event_id: string;
  previous_item_id: string;
  item_id: string;
};

// Conversation events
export type ConversationItemCreated = {
  type: "conversation.item.created";
  event_id: string;
  previous_item_id: string;
  item: {
    id: string;
    object: string;
    type: string;
    status: string;
    role?: string;
    content?: object[];
  };
};

// Response events
export type ResponseCreated = {
  type: "response.created";
  event_id: string;
  response: {
    id: string;
    object: string;
    status: string;
    status_details: object | null;
    output: object[];
    usage: object | null;
  };
};

export type ResponseOutputItemAdded = {
  type: "response.output_item.added";
  event_id: string;
  response_id: string;
  output_index: number;
  item: {
    id: string;
    object: string;
    type: string;
    status: string;
    role?: string;
    content?: object[];
  };
};

export type ResponseContentPartAdded = {
  type: "response.content_part.added";
  event_id: string;
  response_id: string;
  item_id: string;
  output_index: number;
  content_index: number;
  part: {
    type: string;
    text?: string;
    audio?: string;
    transcript?: string;
  };
};

export type ResponseContentPartDone = {
  type: "response.content_part.done";
  event_id: string;
  response_id: string;
  item_id: string;
  output_index: number;
  content_index: number;
  part: {
    type: string;
    text?: string;
    audio?: string;
    transcript?: string;
  };
};

export type ResponseTextDelta = {
  type: "response.text.delta";
  event_id: string;
  response_id: string;
  item_id: string;
  output_index: number;
  content_index: number;
  delta: string;
};

export type ResponseTextDone = {
  type: "response.text.done";
  event_id: string;
  response_id: string;
  item_id: string;
  output_index: number;
  content_index: number;
  text: string;
};

export type ResponseAudioDone = {
  type: "response.audio.done";
  event_id: string;
  response_id: string;
  item_id: string;
  output_index: number;
  content_index: number;
};

export type ResponseFunctionCallArgumentsDelta = {
  type: "response.function_call_arguments.delta";
  event_id: string;
  response_id: string;
  item_id: string;
  output_index: number;
  call_id: string;
  delta: string;
};

export type ResponseOutputItemDone = {
  type: "response.output_item.done";
  event_id: string;
  response_id: string;
  output_index: number;
  item: {
    id: string;
    object: string;
    type: string;
    status: string;
    role?: string;
    content?: object[];
  };
};

// Rate limits event
export type RateLimitsUpdated = {
  type: "rate_limits.updated";
  event_id: string;
  rate_limits: {
    name: string;
    limit: number;
    remaining: number;
    reset_seconds: number;
  }[];
};

// Error event
export type ErrorEvent = {
  type: "error";
  event_id: string;
  error: {
    type: "invalid_request_error" | "authentication_error" | "permission_error" | "not_found_error" | "request_too_large" | "rate_limit_exceeded" | "api_error" | "overloaded_error" | "network_error";
    code: string;
    message: string;
    param?: string;
    event_id?: string;
  };
};

// Enhanced error types for better error handling
export type RealtimeError = {
  type: string;
  code: string;
  message: string;
  param?: string;
  event_id?: string;
  details?: Record<string, any>;
};

export type ErrorResponse = {
  error: RealtimeError;
  timestamp: number;
  request_id?: string;
};

export enum EToolNames {
  "get_user_input" = "get_user_input",
};
export enum EUserIntents {
  create_claim = "create_claim",
  view_existing_claim = "view_existing_claim",
}
export type ToolResult = {
  intent: EUserIntents.create_claim | EUserIntents.view_existing_claim;
};

export enum EUserInfoFormStatus {
  hidden = "hidden",
  open = "open",
  submitted = "submitted",
}